// Recruitment — AI-powered candidate screening for Limpopo + Pretoria
// branches. Admin-only. Dark staff theme.
//
// Layout: Two panels on desktop (openings left, candidates right),
// stacked on mobile. Branch selector persists to localStorage.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Plus, Users, Upload, Loader, RefreshCw, Download, X, Star,
  AlertTriangle, CheckCircle, SkipForward, Eye, Search,
  Briefcase, MapPin, Calendar, Trash2, ChevronDown, MoreVertical,
  FileText, Award,
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { ADMIN_EMAILS } from '../config/adminConfig'
import {
  getJobOpenings, refreshJobOpenings, closeJobOpening, deleteJobOpening,
  getCandidates, refreshCandidates, updateCandidate, deleteCandidate,
  summariseOpenings, BRANCHES, CANDIDATE_STATUSES,
} from '../lib/recruitmentStore'
import { processCVBatch, rescoreAllCandidates } from '../lib/cvParser'
import NewOpeningModal from '../components/recruitment/NewOpeningModal'
import CandidateDrawer from '../components/recruitment/CandidateDrawer'
import { exportShortlistPdf } from '../components/recruitment/exportShortlistPdf'

const HIDDEN_INPUT_STYLE = {
  position: 'absolute', width: '1px', height: '1px',
  opacity: 0, overflow: 'hidden', pointerEvents: 'none',
}

const BRANCH_LABELS = {
  limpopo: '🏔 Limpopo',
  pretoria: '🏙 Pretoria',
}

const STATUS_BADGE = {
  open: 'bg-green-900/40 text-green-400 border-green-700/50',
  closed: 'bg-red-900/40 text-red-400 border-red-700/50',
  filled: 'bg-brand-900/40 text-brand-400 border-brand-700/50',
}

function scoreTier(score) {
  const s = Number(score) || 0
  if (s >= 90) return { label: 'Excellent', colour: 'text-green-400', bg: 'bg-green-900/40', border: 'border-green-700/50', dot: '🟢' }
  if (s >= 70) return { label: 'Good', colour: 'text-blue-400', bg: 'bg-blue-900/40', border: 'border-blue-700/50', dot: '🔵' }
  if (s >= 50) return { label: 'Average', colour: 'text-amber-400', bg: 'bg-amber-900/40', border: 'border-amber-700/50', dot: '🟡' }
  return { label: 'Weak', colour: 'text-red-400', bg: 'bg-red-900/40', border: 'border-red-700/50', dot: '🔴' }
}

function formatDate(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch { return iso.slice(0, 10) }
}

// ─── Page ─────────────────────────────────────────────────────────

export default function Recruitment() {
  const { user, userProfile } = useAuth()
  const { addToast } = useToast()

  const isAdmin = ADMIN_EMAILS.includes(user?.email) || userProfile?.role === 'admin'
  if (!isAdmin) return <Navigate to="/app/dashboard" replace />

  const [branch, setBranch] = useState(() => {
    try { return localStorage.getItem('desres_recruitment_branch') || 'limpopo' }
    catch { return 'limpopo' }
  })
  const [refreshKey, setRefreshKey] = useState(0)
  const bump = () => setRefreshKey(k => k + 1)

  const [selectedOpeningId, setSelectedOpeningId] = useState(null)
  const [showNewOpening, setShowNewOpening] = useState(false)
  const [drawerCandidate, setDrawerCandidate] = useState(null)
  const [showExport, setShowExport] = useState(false)
  const [openingMenu, setOpeningMenu] = useState(null) // id of opening whose menu is open

  // Batch upload
  const fileInputRef = useRef(null)
  const [pendingFiles, setPendingFiles] = useState([])
  const [batchProgress, setBatchProgress] = useState(null) // { total, results[] }
  const [isProcessing, setIsProcessing] = useState(false)
  const [isRescoring, setIsRescoring] = useState(false)

  // Candidate filters
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortKey, setSortKey] = useState('score')
  const [searchQ, setSearchQ] = useState('')

  // Initial load + branch change
  useEffect(() => {
    try { localStorage.setItem('desres_recruitment_branch', branch) } catch { /* ignore */ }
    let cancelled = false
    async function init() {
      try {
        await Promise.all([
          refreshJobOpenings(branch),
          refreshCandidates(branch),
        ])
      } catch (err) {
        console.warn('[Recruitment] init refresh failed:', err?.message)
      }
      if (!cancelled) bump()
    }
    init()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch])

  const allOpenings = useMemo(
    () => summariseOpenings(getJobOpenings(branch), getCandidates(branch)),
    [branch, refreshKey]
  )
  const openings = useMemo(
    () => allOpenings.slice().sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
    [allOpenings]
  )
  const selectedOpening = useMemo(
    () => openings.find(o => o.id === selectedOpeningId) || null,
    [openings, selectedOpeningId]
  )

  const candidates = useMemo(() => {
    if (!selectedOpeningId) return []
    let list = getCandidates(branch, { jobOpeningId: selectedOpeningId })
    if (statusFilter === 'shortlisted') list = list.filter(c => c.shortlisted)
    else if (statusFilter !== 'all') list = list.filter(c => c.status === statusFilter)
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase()
      list = list.filter(c =>
        (c.fullName || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.location || '').toLowerCase().includes(q) ||
        (c.skills || []).some(s => String(s).toLowerCase().includes(q))
      )
    }
    const sorted = list.slice()
    if (sortKey === 'score') sorted.sort((a, b) => (b.rankingScore || 0) - (a.rankingScore || 0))
    else if (sortKey === 'experience') sorted.sort((a, b) => (b.rankingBreakdown?.experienceScore || 0) - (a.rankingBreakdown?.experienceScore || 0))
    else if (sortKey === 'education') sorted.sort((a, b) => (b.rankingBreakdown?.educationScore || 0) - (a.rankingBreakdown?.educationScore || 0))
    else if (sortKey === 'age') sorted.sort((a, b) => (b.rankingBreakdown?.ageScore || 0) - (a.rankingBreakdown?.ageScore || 0))
    return sorted
  }, [branch, selectedOpeningId, statusFilter, searchQ, sortKey, refreshKey])

  const candidateCounts = useMemo(() => {
    if (!selectedOpeningId) return { all: 0, shortlisted: 0, pending: 0, rejected: 0, interviewed: 0, hired: 0 }
    const list = getCandidates(branch, { jobOpeningId: selectedOpeningId })
    const counts = { all: list.length, shortlisted: 0 }
    for (const s of CANDIDATE_STATUSES) counts[s] = 0
    for (const c of list) {
      if (c.shortlisted) counts.shortlisted++
      counts[c.status || 'pending'] = (counts[c.status || 'pending'] || 0) + 1
    }
    return counts
  }, [branch, selectedOpeningId, refreshKey])

  // ── Handlers ──
  function handleFileSelect(fileList) {
    const arr = Array.from(fileList || []).filter(f => String(f.name || '').toLowerCase().endsWith('.pdf'))
    if (arr.length > 20) {
      addToast(`Only the first 20 files will be processed (${arr.length - 20} skipped)`, 'warning')
    }
    setPendingFiles(arr.slice(0, 20))
  }

  async function handleStartAnalysis() {
    if (!selectedOpening || pendingFiles.length === 0) return
    setIsProcessing(true)
    setBatchProgress({ total: pendingFiles.length, results: pendingFiles.map(f => ({
      fileName: f.name, status: 'queued',
    })) })

    try {
      const result = await processCVBatch(pendingFiles, selectedOpening, branch, (update) => {
        setBatchProgress(prev => {
          if (!prev) return prev
          const results = prev.results.slice()
          const idx = results.findIndex(r => r.fileName === update.fileName)
          const entry = {
            fileName: update.fileName,
            status: update.status,
            step: update.step || '',
            error: update.error || '',
            candidate: update.candidate || null,
          }
          if (idx >= 0) results[idx] = entry
          else results.push(entry)
          return { total: prev.total, current: update.current, results }
        })
      })
      addToast(
        `${result.processed} analysed · ${result.duplicates} duplicates · ${result.failed} failed`,
        result.failed > 0 ? 'warning' : 'success'
      )
    } catch (err) {
      addToast(`Batch failed: ${err.message}`, 'error')
    } finally {
      setIsProcessing(false)
      setPendingFiles([])
      if (fileInputRef.current) fileInputRef.current.value = ''
      bump()
    }
  }

  async function handleRescoreAll() {
    if (!selectedOpening || isRescoring) return
    setIsRescoring(true)
    try {
      const result = await rescoreAllCandidates(selectedOpening, branch, null)
      addToast(`Re-scored ${result.rescored} candidates${result.failed ? `, ${result.failed} failed` : ''}`,
        result.failed ? 'warning' : 'success')
      bump()
    } catch (err) {
      addToast(`Re-score failed: ${err.message}`, 'error')
    } finally {
      setIsRescoring(false)
    }
  }

  async function handleCloseOpening(id) {
    try {
      await closeJobOpening(id, branch)
      addToast('Opening closed', 'success')
      setOpeningMenu(null)
      bump()
    } catch (err) {
      addToast(`Failed to close: ${err.message}`, 'error')
    }
  }

  async function handleDeleteOpening(id) {
    if (!confirm('Delete this job opening? Candidates will NOT be deleted.')) return
    try {
      await deleteJobOpening(id, branch)
      addToast('Opening deleted', 'success')
      setOpeningMenu(null)
      if (selectedOpeningId === id) setSelectedOpeningId(null)
      bump()
    } catch (err) {
      addToast(`Failed to delete: ${err.message}`, 'error')
    }
  }

  async function handleShortlistToggle(candidate) {
    try {
      await updateCandidate(candidate.id, branch, { shortlisted: !candidate.shortlisted })
      bump()
    } catch (err) {
      addToast(`Failed: ${err.message}`, 'error')
    }
  }

  async function handleReject(candidate) {
    try {
      await updateCandidate(candidate.id, branch, { status: 'rejected', shortlisted: false })
      addToast('Candidate rejected', 'info')
      bump()
    } catch (err) {
      addToast(`Failed: ${err.message}`, 'error')
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users size={22} className="text-brand-400" /> Recruitment
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">AI-powered candidate screening and ranking</p>
          </div>
          <div className="flex items-center gap-1.5 bg-gray-900 border border-gray-800 rounded-lg p-1">
            {BRANCHES.map(b => (
              <button
                key={b}
                onClick={() => { setBranch(b); setSelectedOpeningId(null); setPendingFiles([]); setBatchProgress(null) }}
                className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                  branch === b ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {BRANCH_LABELS[b]}
              </button>
            ))}
          </div>
        </div>

        {/* Two-panel layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* ── Job Openings Panel ── */}
          <section className="lg:col-span-4 bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-white flex items-center gap-1.5">
                <Briefcase size={14} className="text-brand-400" /> Job Openings
              </h2>
              <button
                onClick={() => setShowNewOpening(true)}
                className="flex items-center gap-1 bg-brand-600 hover:bg-brand-500 text-white px-2.5 py-1 rounded text-xs"
              >
                <Plus size={12} /> New Opening
              </button>
            </div>

            {openings.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-6">
                No openings yet. Create one to start screening candidates.
              </p>
            ) : (
              <div className="space-y-2">
                {openings.map(op => (
                  <OpeningCard
                    key={op.id}
                    opening={op}
                    isSelected={op.id === selectedOpeningId}
                    onSelect={() => setSelectedOpeningId(op.id)}
                    menuOpen={openingMenu === op.id}
                    toggleMenu={() => setOpeningMenu(openingMenu === op.id ? null : op.id)}
                    onClose={() => handleCloseOpening(op.id)}
                    onDelete={() => handleDeleteOpening(op.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* ── Candidates Panel ── */}
          <section className="lg:col-span-8 bg-gray-900 border border-gray-800 rounded-lg p-4">
            {!selectedOpening ? (
              <div className="text-center py-16 text-gray-500">
                <Users size={36} className="mx-auto mb-3 text-gray-700" />
                <p className="text-sm">Select a job opening to view candidates</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-bold text-white truncate">{selectedOpening.jobTitle}</h2>
                    <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                      <span className="bg-gray-800 text-gray-300 px-2 py-0.5 rounded border border-gray-700">
                        {selectedOpening.department}
                      </span>
                      <span className={`px-2 py-0.5 rounded border ${STATUS_BADGE[selectedOpening.status] || STATUS_BADGE.open}`}>
                        {selectedOpening.status}
                      </span>
                      <span className="text-gray-500">
                        {candidateCounts.all} candidate{candidateCounts.all !== 1 ? 's' : ''} · {candidateCounts.shortlisted} shortlisted
                        {selectedOpening.topScore > 0 && ` · Top score: ${selectedOpening.topScore}/100`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handleRescoreAll}
                      disabled={isRescoring || isProcessing || candidateCounts.all === 0}
                      className="flex items-center gap-1 bg-amber-950/40 hover:bg-amber-900/40 disabled:opacity-40 text-amber-300 border border-amber-700/50 px-2.5 py-1.5 rounded text-xs"
                    >
                      {isRescoring ? <Loader size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                      Re-score All
                    </button>
                    <button
                      onClick={() => setShowExport(true)}
                      disabled={candidateCounts.all === 0}
                      className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-200 border border-gray-700 px-2.5 py-1.5 rounded text-xs"
                    >
                      <Download size={11} /> Export PDF
                    </button>
                  </div>
                </div>

                {/* Upload area (only when opening is 'open') */}
                {selectedOpening.status === 'open' && (
                  <div className="mb-4 border border-dashed border-gray-700 rounded-lg p-4 bg-gray-800/30">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-sm text-gray-200 font-medium flex items-center gap-1.5">
                          <Upload size={14} /> Upload CV PDFs
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Up to 20 PDFs. iOS-safe. {pendingFiles.length > 0 && (
                            <span className="text-brand-400">{pendingFiles.length} selected</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isProcessing}
                          className="flex items-center gap-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-gray-200 px-3 py-1.5 rounded text-xs"
                        >
                          <Upload size={12} /> Choose Files
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,application/pdf,application/x-pdf"
                          multiple
                          onChange={e => handleFileSelect(e.target.files)}
                          style={HIDDEN_INPUT_STYLE}
                        />
                        <button
                          onClick={handleStartAnalysis}
                          disabled={isProcessing || pendingFiles.length === 0}
                          className="flex items-center gap-1 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-white px-3 py-1.5 rounded text-xs"
                        >
                          {isProcessing ? <Loader size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                          Start Analysis
                        </button>
                      </div>
                    </div>

                    {batchProgress && (
                      <div className="mt-3 border-t border-gray-700 pt-3">
                        <p className="text-xs text-gray-300 mb-2">
                          {isProcessing
                            ? `Analysing CVs... ${batchProgress.current || 0} of ${batchProgress.total} complete`
                            : `Processed ${batchProgress.total} CVs`}
                        </p>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {batchProgress.results.map((r, i) => (
                            <BatchResultLine key={i} result={r} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Filters + sort + search */}
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <div className="flex items-center gap-1 text-xs bg-gray-800 border border-gray-700 rounded p-0.5">
                    {[
                      { key: 'all', label: `All (${candidateCounts.all})` },
                      { key: 'shortlisted', label: `Shortlisted (${candidateCounts.shortlisted})` },
                      { key: 'pending', label: `Pending (${candidateCounts.pending})` },
                      { key: 'rejected', label: `Rejected (${candidateCounts.rejected})` },
                      { key: 'interviewed', label: `Interviewed (${candidateCounts.interviewed})` },
                      { key: 'hired', label: `Hired (${candidateCounts.hired})` },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => setStatusFilter(opt.key)}
                        className={`px-2 py-0.5 rounded transition-colors ${
                          statusFilter === opt.key ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <select
                    value={sortKey}
                    onChange={e => setSortKey(e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
                  >
                    <option value="score">Highest Score</option>
                    <option value="experience">Most Experience</option>
                    <option value="education">Best Education</option>
                    <option value="age">Age Match</option>
                  </select>
                  <div className="flex items-center gap-1 flex-1 min-w-[180px] bg-gray-800 border border-gray-700 rounded px-2">
                    <Search size={12} className="text-gray-500 shrink-0" />
                    <input
                      type="text"
                      value={searchQ}
                      onChange={e => setSearchQ(e.target.value)}
                      placeholder="Search by name, skill, location..."
                      className="bg-transparent text-xs text-white placeholder:text-gray-500 outline-none w-full py-1"
                    />
                  </div>
                </div>

                {/* Candidate list */}
                {candidates.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-8">
                    {candidateCounts.all === 0
                      ? 'No candidates yet. Upload some CVs to get started.'
                      : 'No candidates match these filters.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {candidates.map((c, i) => (
                      <CandidateCard
                        key={c.id}
                        rank={i + 1}
                        candidate={c}
                        onView={() => setDrawerCandidate(c)}
                        onShortlist={() => handleShortlistToggle(c)}
                        onReject={() => handleReject(c)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>

      {/* Modals + drawer */}
      {showNewOpening && (
        <NewOpeningModal
          branch={branch}
          onClose={() => setShowNewOpening(false)}
          onSaved={(saved) => { bump(); setSelectedOpeningId(saved.id) }}
          addToast={addToast}
        />
      )}

      {drawerCandidate && (
        <CandidateDrawer
          candidate={drawerCandidate}
          branch={branch}
          onClose={() => setDrawerCandidate(null)}
          onChange={bump}
          addToast={addToast}
        />
      )}

      {showExport && selectedOpening && (
        <ExportShortlistModal
          opening={selectedOpening}
          candidates={getCandidates(branch, { jobOpeningId: selectedOpening.id })}
          branch={branch}
          onClose={() => setShowExport(false)}
          addToast={addToast}
        />
      )}
    </div>
  )
}

// ─── OpeningCard ──────────────────────────────────────────────────

function OpeningCard({ opening, isSelected, onSelect, menuOpen, toggleMenu, onClose, onDelete }) {
  return (
    <div className={`border rounded-lg p-3 transition-colors cursor-pointer ${
      isSelected ? 'bg-brand-950/30 border-brand-700/50' : 'bg-gray-800/40 border-gray-700 hover:border-gray-600'
    }`} onClick={onSelect}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{opening.jobTitle}</p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className="text-[10px] bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">{opening.department}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_BADGE[opening.status] || STATUS_BADGE.open}`}>
              {opening.status}
            </span>
          </div>
          <div className="mt-1.5 text-[11px] text-gray-400">
            {opening.candidateCount} candidate{opening.candidateCount !== 1 ? 's' : ''}
            {opening.topScore > 0 && <> · Top {opening.topCandidateName} ({opening.topScore}/100)</>}
          </div>
          <div className="text-[10px] text-gray-500">
            {opening.shortlistedCount} shortlisted · Created {formatDate(opening.createdAt)}
          </div>
        </div>
        <div className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); toggleMenu() }}
            className="text-gray-500 hover:text-white p-1"
          >
            <MoreVertical size={13} />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-6 bg-gray-800 border border-gray-700 rounded shadow-xl py-1 z-10 min-w-[140px]"
              onClick={e => e.stopPropagation()}
            >
              {opening.status === 'open' && (
                <button
                  onClick={onClose}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                >
                  <X size={12} /> Close Opening
                </button>
              )}
              <button
                onClick={onDelete}
                className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-gray-700 flex items-center gap-2"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── CandidateCard ────────────────────────────────────────────────

function CandidateCard({ rank, candidate, onView, onShortlist, onReject }) {
  const tier = scoreTier(candidate.rankingScore)
  const topSkills = (candidate.skills || []).slice(0, 3)

  return (
    <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-3 hover:border-gray-600 transition-colors">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 text-center">
          <p className="text-xs text-gray-500">#{rank}</p>
          <div className={`mt-1 inline-flex items-center justify-center w-10 h-10 rounded-full ${tier.bg} ${tier.colour} font-bold text-sm border ${tier.border}`}>
            {candidate.rankingScore || 0}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-white truncate">{candidate.fullName}</p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${tier.bg} ${tier.colour} border ${tier.border}`}>
              {tier.label}
            </span>
            {candidate.recommendation && (
              <span className="text-[10px] text-gray-300 bg-gray-800 border border-gray-700 px-1.5 py-0.5 rounded">
                {candidate.recommendation}
              </span>
            )}
            {candidate.analysisFailed && (
              <span className="text-[10px] text-red-400 bg-red-950/40 border border-red-800/50 px-1.5 py-0.5 rounded">
                Analysis failed
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400 flex-wrap">
            {candidate.phone && <span>📞 {candidate.phone}</span>}
            {candidate.email && <span className="truncate max-w-[180px]">✉ {candidate.email}</span>}
            {candidate.location && <span>📍 {candidate.location}</span>}
            {candidate.age != null && <span>🎂 {candidate.age}</span>}
          </div>

          <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
            {candidate.rankingBreakdown?.experienceScore != null && (
              <span>💼 Experience {candidate.rankingBreakdown.experienceScore}/25</span>
            )}
            {candidate.rankingBreakdown?.educationScore != null && (
              <span>🎓 Education {candidate.rankingBreakdown.educationScore}/20</span>
            )}
          </div>

          {topSkills.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {topSkills.map(s => (
                <span key={s} className="text-[10px] bg-gray-800 text-gray-300 px-1.5 py-0.5 rounded border border-gray-700">
                  {s}
                </span>
              ))}
            </div>
          )}

          {candidate.rankingReason && (
            <p className="text-[11px] text-gray-400 italic mt-1.5 line-clamp-2">"{candidate.rankingReason}"</p>
          )}
        </div>

        <div className="shrink-0 flex flex-col gap-1">
          <button
            onClick={onView}
            className="flex items-center gap-1 bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-[11px]"
            title="View full profile"
          >
            <Eye size={11} /> View
          </button>
          <button
            onClick={onShortlist}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] border ${
              candidate.shortlisted
                ? 'bg-amber-900/40 text-amber-300 border-amber-700/50'
                : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-white'
            }`}
          >
            <Star size={11} fill={candidate.shortlisted ? 'currentColor' : 'none'} />
            {candidate.shortlisted ? 'Shortlisted' : 'Shortlist'}
          </button>
          {candidate.status !== 'rejected' && (
            <button
              onClick={onReject}
              className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/40 text-gray-400 hover:text-red-300 border border-gray-700 hover:border-red-700/50 px-2 py-1 rounded text-[11px]"
            >
              <X size={11} /> Reject
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── BatchResultLine ──────────────────────────────────────────────

function BatchResultLine({ result }) {
  if (result.status === 'done' && result.candidate) {
    const tier = scoreTier(result.candidate.rankingScore)
    return (
      <div className="flex items-center gap-2 text-[11px]">
        <CheckCircle size={11} className="text-green-400 shrink-0" />
        <span className="text-gray-300 truncate flex-1">{result.fileName}</span>
        <span className={`${tier.colour}`}>{result.candidate.rankingScore}/100 ({result.candidate.recommendation})</span>
      </div>
    )
  }
  if (result.status === 'processing' || result.status === 'queued') {
    return (
      <div className="flex items-center gap-2 text-[11px]">
        <Loader size={11} className="text-brand-400 animate-spin shrink-0" />
        <span className="text-gray-300 truncate flex-1">{result.fileName}</span>
        <span className="text-gray-500 capitalize">{result.step || result.status}</span>
      </div>
    )
  }
  if (result.status === 'duplicate') {
    return (
      <div className="flex items-center gap-2 text-[11px]">
        <SkipForward size={11} className="text-gray-400 shrink-0" />
        <span className="text-gray-400 truncate flex-1">{result.fileName}</span>
        <span className="text-gray-500">Already analysed, skipped</span>
      </div>
    )
  }
  if (result.status === 'failed') {
    return (
      <div className="flex items-center gap-2 text-[11px]">
        <AlertTriangle size={11} className="text-red-400 shrink-0" />
        <span className="text-gray-300 truncate flex-1">{result.fileName}</span>
        <span className="text-red-400 truncate max-w-[180px]">{result.error || 'failed'}</span>
      </div>
    )
  }
  return null
}

// ─── ExportShortlistModal ─────────────────────────────────────────

function ExportShortlistModal({ opening, candidates, branch, onClose, addToast }) {
  const [count, setCount] = useState(5)
  const [generating, setGenerating] = useState(false)

  const shortlisted = candidates.filter(c => c.shortlisted)
  const source = count === 'all' ? shortlisted : candidates

  async function handleGenerate() {
    setGenerating(true)
    try {
      const topN = count === 'all' ? shortlisted.length : Number(count) || 5
      await exportShortlistPdf({ opening, candidates: source, branch, topN })
      addToast('PDF exported', 'success')
      onClose()
    } catch (err) {
      addToast(`Export failed: ${err.message}`, 'error')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <FileText size={16} /> Export Shortlist
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-400">
            Generates a PDF report with one candidate per page, ranked by score, for the hiring manager.
          </p>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">How many candidates?</label>
            <select
              value={count}
              onChange={e => setCount(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
            >
              <option value={3}>Top 3 by score</option>
              <option value={5}>Top 5 by score</option>
              <option value={10}>Top 10 by score</option>
              <option value="all">All shortlisted ({shortlisted.length})</option>
            </select>
          </div>
          {count === 'all' && shortlisted.length === 0 && (
            <p className="text-xs text-amber-400">No candidates are shortlisted yet.</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800">
          <button onClick={onClose} disabled={generating} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white">
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || (count === 'all' && shortlisted.length === 0)}
            className="flex items-center gap-1 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-white px-4 py-1.5 rounded text-sm"
          >
            {generating ? <Loader size={13} className="animate-spin" /> : <Download size={13} />}
            Generate PDF
          </button>
        </div>
      </div>
    </div>
  )
}
